import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { ArrowRight, Camera, Sparkles, Music, Award, Film, Tv, Users, Building2, ShieldCheck, Send, Instagram } from "lucide-react";
import { toast } from "sonner";

const HERO_IMG = "https://customer-assets-cm19k8pv.emergentagent.net/job_sv-talent-hub/artifacts/k4hgwx7q_IMG_8662.webp";
const IG_URL = "https://www.instagram.com/svproduction6?stkn=MWZoMm5wc2lkcDk4bQ==";

const SERVICES = [
  { I: Camera, l: "Modeling" }, { I: Sparkles, l: "Fashion" },
  { I: Music, l: "Music Videos" }, { I: Award, l: "Advertising" },
  { I: Film, l: "Editorial" }, { I: Tv, l: "Acting & Talent" },
  { I: Users, l: "Casting" }, { I: Building2, l: "Artist Management" },
];

const DEFAULT_STATS = [
  { n: "500+", l: "TALENTS" },
  { n: "100+", l: "CAMPAIGNS" },
  { n: "50+", l: "BRANDS" },
  { n: "5+", l: "YEARS" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", project_type: "", message: "" });

  useEffect(() => {
    api.get("/talents?limit=8&sort=featured").then((r) => setFeatured(r.data?.items || [])).catch(() => {});
    api.get("/site-stats").then((r) => { if (r.data?.stats?.length) setStats(r.data.stats); }).catch(() => {});
  }, []);

  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", { ...contact, requirement: contact.project_type, company: "" });
      toast.success("Inquiry sent. Our team will reach out shortly.");
      setContact({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch { toast.error("Could not send inquiry."); }
  };

  return (
    <div className="min-h-screen bg-[#050506]">
      <Header />

      {/* HERO — editorial split */}
      <section className="relative overflow-hidden min-h-[calc(100vh-6rem)] -mt-24 pt-24">
        {/* Right-side hero image */}
        <div className="absolute inset-0">
          <div className="absolute inset-y-0 right-0 w-full lg:w-[62%] xl:w-[58%]">
            <img src={HERO_IMG} alt="SV Talent Today — Vishakha Raghav" className="w-full h-full object-cover object-[center_top]" />
          </div>
          {/* Left cinematic gradient over the model for readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#050506] via-[#050506]/85 to-transparent lg:via-[#050506]/60 lg:to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050506]/40 via-transparent to-[#050506]/80" />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 py-12 lg:py-16 min-h-[calc(100vh-6rem)] flex flex-col justify-between">
          {/* CENTER ROW — split content */}
          <div className="grid grid-cols-12 gap-6 flex-1 items-center">
            {/* LEFT copy */}
            <div className="col-span-12 lg:col-span-7 sv-reveal">
              <div className="font-mono-label text-[10px] text-[#F4EFE4] tracking-[0.42em] leading-[1.8]">
                MODELING &nbsp;·&nbsp; TALENT &nbsp;·&nbsp; CASTING
                <br />ARTIST MANAGEMENT
              </div>
              <h1 className="font-serif-hero text-[64px] sm:text-[92px] lg:text-[128px] leading-[0.92] text-[#F4EFE4] mt-6">
                DREAMS<br />
                <span className="text-gold-gradient">BELONG HERE.</span>
              </h1>
              <p className="mt-8 max-w-xl text-[15px] sm:text-base text-[#CDC7BA] leading-relaxed">
                SV Talent Today is a premium modeling and talent agency<br className="hidden sm:block" />
                representing distinctive faces and emerging talent for<br className="hidden sm:block" />
                fashion, music, advertising and entertainment.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 sv-reveal sv-reveal-delay-2">
                <Link to="/membership" data-testid="hero-become-member"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-[#C5A059] text-[#0A0A0B] font-mono-label text-[11px] tracking-[0.32em] hover:bg-[#D9B672] transition">
                  BECOME A MEMBER <ArrowRight size={14} />
                </Link>
                <Link to="/talent" data-testid="hero-explore-talent"
                  className="inline-flex items-center gap-3 px-8 py-4 border border-[#C5A059] text-[#F4E4BC] font-mono-label text-[11px] tracking-[0.32em] hover:bg-[#C5A059]/10 transition">
                  EXPLORE TALENT <ArrowRight size={14} />
                </Link>
              </div>
              {/* Stats */}
              <div className="mt-14 pt-8 border-t border-[rgba(197,160,89,0.28)] max-w-2xl">
                <div className="grid grid-cols-4 gap-4">
                  {stats.map((s, i) => (
                    <div key={i} className={`${i > 0 ? "border-l border-[rgba(197,160,89,0.22)] pl-4" : ""}`} data-testid={`hero-stat-${s.l.toLowerCase()}`}>
                      <div className="font-serif-hero text-3xl sm:text-4xl text-[#F4EFE4] leading-none">{s.n}</div>
                      <div className="font-mono-label text-[9px] text-[#8A8478] tracking-[0.32em] mt-2">{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT column — vertical block */}
            <div className="hidden lg:flex col-span-5 flex-col items-end justify-between h-full py-8">
              <div className="flex items-start gap-4">
                <div className="w-px h-24 bg-[rgba(244,239,228,0.35)] mt-1" />
                <div className="font-mono-label text-[10px] text-[#F4EFE4] tracking-[0.42em] leading-[1.8]">
                  NEW<br />FACES<br />BIGGER<br />OPPORTUNITIES.
                </div>
              </div>
              <div className="text-right">
                <div className="font-serif-hero text-3xl text-[#F4E4BC] leading-none">SV</div>
                <div className="font-display-cinzel text-[10px] text-[#F4EFE4] tracking-[0.42em] mt-1">TALENT TODAY</div>
              </div>
            </div>
          </div>

          {/* BOTTOM ROW */}
          <div className="mt-10 flex flex-wrap gap-6 justify-between items-end">
            <div className="flex flex-col items-start gap-4" data-testid="hero-scroll">
              <div className="font-mono-label text-[10px] text-[#F4EFE4] tracking-[0.42em]">SCROLL TO EXPLORE</div>
              <div className="w-5 h-8 border border-[rgba(244,239,228,0.5)] rounded-full flex justify-center pt-1.5">
                <span className="w-[3px] h-2 bg-[#C5A059] rounded-full animate-bounce" />
              </div>
              <div className="w-px h-14 bg-[rgba(244,239,228,0.25)]" />
            </div>
            <div className="text-right ml-auto">
              <div className="inline-block border-t border-[rgba(197,160,89,0.35)] pt-4 pl-16">
                <div className="font-mono-label text-[10px] text-[#F4EFE4] tracking-[0.42em] leading-[1.9]">
                  BE SEEN.<br />BE DISCOVERED.<br />BE PART OF SOMETHING BIGGER.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED TALENT */}
      {featured.length > 0 ? (
        <section className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 py-24 lg:py-32">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
            <div>
              <div className="sv-overline mb-4">Featured Talent</div>
              <h2 className="font-serif-hero text-3xl lg:text-6xl text-[#F4EFE4]">Distinctive faces.<br />Emerging talent.</h2>
            </div>
            <Link to="/talent" className="hidden sm:inline-flex items-center gap-2 text-[11px] font-mono-label text-[#C5A059] tracking-[0.3em]">
              BROWSE ALL <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {featured.map((t) => {
              const gender = (t.data?.gender || "").toLowerCase();
              const bucket = gender === "female" ? "women" : gender === "male" ? "men" : "talent";
              const to = t.data?.slug ? `/${bucket}/${t.data.slug}` : `/talent/${t.user_id}`;
              return (
                <Link to={to} key={t.user_id} className="talent-card sv-card overflow-hidden group">
                  <div className="aspect-[3/4] overflow-hidden bg-[#131316]">
                    {t.data?.profile_photo
                      ? <img src={t.data.profile_photo} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt={t.full_name} loading="lazy" />
                      : <div className="w-full h-full grid place-items-center text-[#4A4842] font-mono-label text-[10px]">No Image</div>}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="font-serif-editorial text-lg text-[#F4EFE4]">{t.full_name}</div>
                      <ShieldCheck size={14} className="text-[#C5A059]" />
                    </div>
                    <div className="text-xs text-[#8A8478] mt-0.5">{t.data?.categories?.[0]} · {t.data?.city}</div>
                    {t.data?.height && <div className="text-[10px] text-[#8A8478] mt-1 font-mono-label tracking-[0.2em]">{t.data.height}</div>}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      ) : (
        <section className="max-w-[1200px] mx-auto px-4 sm:px-8 lg:px-12 py-24 text-center">
          <div className="sv-overline mb-4">Featured Talent</div>
          <h2 className="font-serif-hero text-3xl lg:text-5xl text-[#F4EFE4]">The roster is being curated.</h2>
          <p className="mt-4 text-[#CDC7BA] max-w-xl mx-auto">Approved talent will appear here.</p>
        </section>
      )}

      {/* CATEGORY tri-panel */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 pb-24">
        <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
          {[
            { label: "Women", to: "/women", tag: "01" },
            { label: "Men", to: "/men", tag: "02" },
            { label: "Talent", to: "/talent", tag: "03" },
          ].map((c) => (
            <Link key={c.label} to={c.to} data-testid={`cat-${c.label.toLowerCase()}`}
              className="relative aspect-[3/4] rounded-xl overflow-hidden group border border-[rgba(197,160,89,0.15)] bg-gradient-to-b from-[#131316] to-[#0A0A0B] flex flex-col justify-end p-8 hover:border-[rgba(197,160,89,0.5)] transition">
              <div className="absolute top-6 left-6 font-mono-label text-[10px] text-[#C5A059] tracking-[0.3em]">{c.tag}</div>
              <div className="font-serif-hero text-5xl lg:text-6xl text-[#F4E4BC] leading-none tracking-tight uppercase">{c.label}</div>
              <div className="mt-4 flex items-center gap-2 text-[10px] font-mono-label text-[#CDC7BA] tracking-[0.3em]">
                EXPLORE <ArrowRight size={14} className="group-hover:translate-x-2 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WHAT WE DO */}
      <section id="services" className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 py-24">
        <div className="text-center mb-14">
          <div className="sv-overline mb-4">What We Do</div>
          <h2 className="font-serif-hero text-3xl lg:text-6xl text-[#F4EFE4]">Casting is storytelling.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
          {SERVICES.map((s) => (
            <div key={s.l} className="sv-card p-6 lg:p-8 text-center">
              <s.I size={26} className="text-[#C5A059] mx-auto mb-4" />
              <div className="font-display-cinzel text-[13px] text-[#F4EFE4] tracking-[0.24em]">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* JOIN CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-[#3D0A14]/40 via-[#4A0B15]/30 to-transparent border-y border-[rgba(255,255,255,0.06)]">
        <div className="max-w-3xl mx-auto text-center px-4">
          <div className="sv-overline mb-4">Ready to be discovered?</div>
          <h2 className="font-serif-hero text-3xl lg:text-6xl text-[#F4EFE4]">Join SV Talent Today.</h2>
          <p className="mt-6 text-[#CDC7BA]">Choose a membership, build your talent profile and be considered for casting opportunities across fashion, music, film, advertising and digital.</p>
          <Link to="/membership" data-testid="home-join-cta" className="inline-flex items-center gap-3 mt-8 px-8 py-4 bg-[#C5A059] text-[#0A0A0B] font-mono-label text-[11px] tracking-[0.32em] hover:bg-[#D9B672] transition">JOIN SV TALENT — ₹599</Link>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="max-w-3xl mx-auto text-center px-4 py-20">
        <div className="sv-overline mb-4">Follow The Cast</div>
        <h2 className="font-serif-hero text-3xl lg:text-5xl text-[#F4EFE4]">@svproduction6</h2>
        <a href={IG_URL} target="_blank" rel="noopener noreferrer" data-testid="ig-follow"
          className="inline-flex items-center gap-2 mt-8 px-8 py-4 border border-[#C5A059] text-[#F4E4BC] font-mono-label text-[11px] tracking-[0.32em] hover:bg-[#C5A059]/10 transition">
          <Instagram size={14} />FOLLOW ON INSTAGRAM
        </a>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-20 border-t border-[rgba(255,255,255,0.06)]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <div className="sv-overline mb-4">Contact</div>
            <h2 className="font-serif-hero text-3xl lg:text-6xl text-[#F4EFE4]">Let's create<br />something.</h2>
            <div className="mt-8 space-y-3 text-sm text-[#CDC7BA]">
              <div><span className="font-mono-label text-[10px] text-[#C5A059] tracking-[0.3em] mr-3">HQ</span>Mohali, Punjab, India</div>
              <div><a href="tel:+918968681108" className="hover:text-[#F4E4BC]"><span className="font-mono-label text-[10px] text-[#C5A059] tracking-[0.3em] mr-3">PHONE</span>89686 81108</a></div>
              <div><a href={IG_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[#F4E4BC]"><span className="font-mono-label text-[10px] text-[#C5A059] tracking-[0.3em] mr-3">INSTAGRAM</span>@svproduction6</a></div>
            </div>
          </div>
          <form onSubmit={send} className="sv-card p-6 lg:p-8 grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <input required data-testid="contact-name" placeholder="Name" className="sv-input" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
              <input required type="email" data-testid="contact-email" placeholder="Email" className="sv-input" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
              <input data-testid="contact-phone" placeholder="Phone" className="sv-input" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
              <select data-testid="contact-project" className="sv-select" value={contact.project_type} onChange={(e) => setContact({ ...contact, project_type: e.target.value })}>
                <option value="">Project Type</option>
                {["Casting", "Music Video", "Film / OTT", "Fashion", "Advertising", "Editorial", "Talent Enquiry", "Brand Enquiry", "Other"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <textarea required rows={5} data-testid="contact-message" placeholder="Message" className="sv-input" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} />
            <button type="submit" data-testid="contact-send" className="inline-flex items-center gap-2 justify-center px-8 py-4 bg-[#C5A059] text-[#0A0A0B] font-mono-label text-[11px] tracking-[0.32em] hover:bg-[#D9B672] transition"><Send size={14} />SEND INQUIRY</button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
